import { useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import { useTranslation, type Locale } from '@/i18n';

type LanguageOption = {
  code: Locale;
  flagSrc: string;
  nativeLabel: string;
};

type LanguageSwitcherProps = {
  variant?: 'light' | 'map' | 'header';
};

const LANGUAGE_OPTIONS: LanguageOption[] = [
  {
    code: 'en',
    flagSrc: '/images/flags/gb.svg',
    nativeLabel: 'English',
  },
  {
    code: 'da',
    flagSrc: '/images/flags/dk.svg',
    nativeLabel: 'Dansk',
  },
];

const FlagCircle = ({
  flagSrc,
  size = 'md',
}: {
  flagSrc: string;
  size?: 'md' | 'sm';
}) => {
  const sizeClasses =
    size === 'sm'
      ? 'h-[18px] w-[18px] sm:h-5 sm:w-5'
      : 'h-5 w-5 sm:h-[22px] sm:w-[22px]';

  return (
    <img
      alt=''
      aria-hidden
      className={`shrink-0 rounded-full object-cover shadow-[0_1px_3px_rgba(0,0,0,0.18)] ${sizeClasses}`}
      draggable={false}
      src={flagSrc}
    />
  );
};

const LanguageCode = ({
  code,
  isDark,
  isSelected,
}: {
  code: string;
  isDark: boolean;
  isSelected: boolean;
}) => (
  <span
    className={`relative inline-block pb-1 text-[13px] font-semibold uppercase tracking-[0.18em] sm:text-sm ${
      isSelected
        ? isDark
          ? 'text-white'
          : 'text-backgroundGreen'
        : isDark
        ? 'text-white/35 hover:text-white/65'
        : 'text-gray-500 hover:text-gray-700'
    }`}
  >
    {code}
    {isSelected ? (
      <span
        aria-hidden
        className='absolute bottom-0 left-0 right-0 h-[2px] rounded-full bg-greenSecondary'
      />
    ) : null}
  </span>
);

const LanguageSwitcher = ({ variant = 'light' }: LanguageSwitcherProps) => {
  const { locale, setLocale, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected =
    LANGUAGE_OPTIONS.find((option) => option.code === locale) ??
    LANGUAGE_OPTIONS[0];

  const isMap = variant === 'map';
  const isHeader = variant === 'header';
  const isDark = isMap || isHeader;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSelect = (code: Locale) => {
    setLocale(code);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={
        isMap
          ? 'fixed right-5 top-5 z-[10000]'
          : isHeader
          ? 'relative'
          : 'absolute right-0 top-0 z-20 sm:right-2'
      }
    >
      <button
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        aria-label={t('language.label')}
        className='group flex items-center gap-2.5 px-1 py-2 transition-opacity hover:opacity-90 sm:gap-3'
        onClick={() => setIsOpen((open) => !open)}
        type='button'
      >
        <FlagCircle flagSrc={selected.flagSrc} />
        <LanguageCode code={selected.code} isDark={isDark} isSelected />
        <FiChevronDown
          aria-hidden
          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          } ${
            isDark ? 'text-white/45 group-hover:text-white/70' : 'text-gray-400'
          }`}
        />
      </button>

      {isOpen ? (
        <ul
          aria-label={t('language.label')}
          className={`absolute right-0 z-50 mt-1 min-w-[132px] overflow-hidden rounded-xl py-1.5 shadow-[0_16px_40px_rgba(9,45,54,0.2)] ${
            isDark
              ? 'border border-white/10 bg-[#0c3a44]/95 backdrop-blur-md'
              : 'border border-gray-100 bg-white/95 ring-1 ring-black/5 backdrop-blur-md'
          }`}
          role='listbox'
        >
          {LANGUAGE_OPTIONS.map((option) => {
            const isSelected = option.code === locale;

            return (
              <li key={option.code} aria-selected={isSelected} role='option'>
                <button
                  aria-label={option.nativeLabel}
                  className='flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors sm:gap-3'
                  onClick={() => handleSelect(option.code)}
                  type='button'
                >
                  <FlagCircle flagSrc={option.flagSrc} size='sm' />
                  <LanguageCode
                    code={option.code}
                    isDark={isDark}
                    isSelected={isSelected}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export { LanguageSwitcher };
